import React from "react";
import ReactDOM from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import "@/index.css";
import "@/App.css";

import PublicHome from "@/pages/PublicHome";
import FoodTrucks from "@/pages/FoodTrucks";
import SubmitEvent from "@/pages/SubmitEvent";
import FavoritesPage from "@/pages/FavoritesPage";
import AdminLogin from "@/pages/AdminLogin";
import AdminLayout from "@/pages/AdminLayout";
import AdminReview from "@/pages/AdminReview";
import AdminEvents from "@/pages/AdminEvents";
import AdminTowns from "@/pages/AdminTowns";
import AdminCategories from "@/pages/AdminCategories";
import AdminSources from "@/pages/AdminSources";
import AdminSyncLogs from "@/pages/AdminSyncLogs";
import AdminMaintenance from "@/pages/AdminMaintenance";
import AdminExtract from "@/pages/AdminExtract";
import AdminSubmissions from "@/pages/AdminSubmissions";
import { getToken } from "@/lib/api";
import { initAnalytics, trackPageview } from "@/lib/analytics";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

// Fire initial pixel/GA load + emit a PageView on every SPA route change.
// Do NOT track admin routes — internal use only. The initial load is also
// gated so the Meta Pixel / GA4 scripts never execute on /admin/* pages.
function AnalyticsRouter() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");
  useEffect(() => {
    if (isAdmin) return;
    initAnalytics();
  }, [isAdmin]);
  useEffect(() => {
    if (isAdmin) return;
    trackPageview(location.pathname + location.search);
  }, [isAdmin, location.pathname, location.search]);
  return null;
}

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, refetchOnWindowFocus: false } },
});

function RequireAuth({ children }) {
  return getToken() ? children : <Navigate to="/admin/login" replace />;
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AnalyticsRouter />
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/" element={<PublicHome />} />
          <Route path="/food-trucks" element={<FoodTrucks />} />
          <Route path="/submit-event" element={<SubmitEvent />} />
          <Route path="/favorites" element={<FavoritesPage />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route
            path="/admin"
            element={
              <RequireAuth>
                <AdminLayout />
              </RequireAuth>
            }
          >
            <Route index element={<Navigate to="review" replace />} />
            <Route path="review" element={<AdminReview />} />
            <Route path="events" element={<AdminEvents />} />
            <Route path="towns" element={<AdminTowns />} />
            <Route path="categories" element={<AdminCategories />} />
            <Route path="sources" element={<AdminSources />} />
            <Route path="sync-logs" element={<AdminSyncLogs />} />
            <Route path="maintenance" element={<AdminMaintenance />} />
            <Route path="extract" element={<AdminExtract />} />
            <Route path="submissions" element={<AdminSubmissions />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
    </HelmetProvider>
  </React.StrictMode>,
);
